// Case14Page.js – Tag structure validated and full page restored
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
const case14Image = require('../assets/RedRockCases/Case14/case 14 placeholder.png');
import exhibitA from '../assets/RedRockCases/Case14/case 14_ExA_Purity Score by Site.png';
import exhibitB from '../assets/RedRockCases/Case14/case 14_ExB_Profit Index By Site.png';
import exhibitC from '../assets/RedRockCases/Case14/case 14_ExC_Environmental Risk Vs Profit.png';
import exhibitD from '../assets/RedRockCases/Case14/case 14_Exd_Risk-Adjusted Profit.png';
// eslint-disable-next-line no-unused-vars -- used in JSX return
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case14Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [currentExhibit, setCurrentExhibit] = useState('A');
  const timerRef = useRef(null);

  const questionData = [
    { 
      id: 1, 
      type: 'mcq', 
      question: 'Based on Exhibit A, which site has the highest purity score?', 
      options: ['Kora', 'Vesta', 'Luno'],
      correctAnswer: 'Kora' 
    },
    { 
      id: 2, 
      type: 'mcq',
      question: 'Which site yields the highest Profit Index before penalties? (Use Exhibit B)',
      options: ['Kora', 'Vesta', 'Luno'],
      correctAnswer: 'Kora'
    },
    { 
      id: 3, 
      type: 'mcq', 
      question: 'According to Exhibit C, which site is at risk of receiving a penalty due to high environmental score?',
      options: ['Kora', 'Vesta', 'Luno'],
      correctAnswer: 'Luno' 
    },
    { 
      id: 4, 
      type: 'mcq', 
      question: 'Calculate the penalty-adjusted profit for Luno based on its environmental score. What is the adjusted profit? (Use Exhibit D)', 
      options: ['$187,500', '$211,875', '$234,000', '$256,000'],
      correctAnswer: '$211,875' 
    },
    { 
      id: 5, 
      type: 'mcq',
      question: 'Which two sites provide the best combined risk-adjusted profit while staying under the environmental penalty threshold? (Use Exhibits C & D)',
      options: ['Kora & Luno', 'Vesta & Kora', 'Vesta & Luno', 'Kora & Vesta'],
      correctAnswer: 'Kora & Vesta'
    },
    { 
      id: 6, 
      type: 'mcq', 
      question: 'True or False: Vesta has the lowest rare mineral percentage but also the lowest waste.', 
      options: ['True', 'False'],
      correctAnswer: 'False' 
    },
    { 
      id: 7, 
      type: 'mcq',
      question: 'Rank the sites from most to least profitable after adjusting for penalties. (Use Exhibit D)',
      options: ['Kora > Luno > Vesta', 'Kora > Vesta > Luno', 'Vesta > Kora > Luno', 'Luno > Kora > Vesta'],
      correctAnswer: 'Kora > Vesta > Luno'
    },
    { 
      id: 8, 
      type: 'multi',
      question: 'Which sites have waste percentages of 40% or more? Select all that apply:',
      parts: [
        {
          label: 'First Site',
          correctAnswers: ['Vesta', 'Luno']
        },
        {
          label: 'Second Site',
          correctAnswers: ['Vesta', 'Luno']
        }
      ],
      validateMultiAnswer: (answers) => {
        // Convert answers to lowercase for case-insensitive comparison
        const answer1 = answers[0]?.toLowerCase();
        const answer2 = answers[1]?.toLowerCase();
        
        // Check if both answers are provided
        if (!answer1 || !answer2) return false;
        
        // Check if answers are different (can't select same site twice)
        if (answer1 === answer2) return false;
        
        // Check if both answers are valid sites
        const validSites = ['vesta', 'luno'];
        return validSites.includes(answer1) && validSites.includes(answer2);
      }
    }
  ];

  // Type B: Move reportAnswers and dynamic scorecard variables to top-level scope
  const reportAnswers = {
    report_site_purity: 'Kora',
    report_profit_index: 'Kora',
    report_env_score: '60',
    report_site_penalty: 'Luno',
    report_adjusted_profit: '211875',
    report_site_1: 'Kora',
    report_site_2: 'Vesta'
  };
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questionData.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 8;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
        // Optional: Stop the timer if finished early
        // clearInterval(timerRef.current);
      }
    }
    // Added questionData.length, timeLeft, and setFinishedEarly to dependencies
  }, [feedback, hasStarted, isComplete, timeLeft, setFinishedEarly]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questionData.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      if (q.validateMultiAnswer) {
        // Use custom validation if provided
        const allPartsCorrect = q.validateMultiAnswer(Object.values(answers[id] || {}));
        setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
      } else {
        // Use default validation
        const allPartsCorrect = q.parts.every((part, index) => {
          const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
          return part.correctAnswers ? 
            part.correctAnswers.map(a => a.toLowerCase()).includes(userAnswer) :
            userAnswer === part.correctAnswer.toLowerCase();
        });
        setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
      }
    } else {
      const userAnswer = (answers[id] || '').trim().toLowerCase();
      const correctAnswer = q.correctAnswer.toLowerCase();
      setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const handleMCQSelect = (id, optionLabel) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: optionLabel }));
    const q = questionData.find((q) => q.id === id);
    setFeedback((prev) => ({ ...prev, [id]: optionLabel === q.correctAnswer ? 'correct' : 'incorrect' }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    clearInterval(timerRef.current);
    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });
    setFinishedEarly(true);
    setTimeExpired(true);
    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDragStart = (e, value, label) => {
    if (e.target.closest('.notebook-content')) return;
    const data = {
      value: value,
      label: label
    };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  const handleDrop = (e) => {
    if (!hasStarted) return;
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const parsedData = JSON.parse(data);
      if (parsedData.value && parsedData.label) {
        setNotebookItems(prev => [...prev, parsedData]);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    e.currentTarget.classList.remove('bg-blue-50');
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  return (
    <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
      <SEO noIndex={true} />
      {/* Side Navigation */}
      <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
        <div className="group relative">
        <a 
          href="#problem" 
            className="flex items-center"
          aria-label="Problem Section"
        >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
            Problem
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <PencilIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
          <a 
            href="#investigation" 
            className="flex items-center"
            aria-label="Investigation Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Investigation
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <BoltIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
          <a 
            href="#exhibits" 
            className="flex items-center"
            aria-label="Exhibits Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Exhibits
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#questions" 
            className="flex items-center"
            aria-label="Analysis Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Analysis
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <ChartBarIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
        <a 
          href="#report" 
            className="flex items-center"
          aria-label="Report Section"
        >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
            Report
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <LightBulbIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>
      </div>

      {/* Hero Section - Enhanced gradient */}
      <section className="sticky top-[50px] z-30 bg-gradient-to-br from-blue-200 to-white pb-4 relative">
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 14: DeepCurrent Minerals</h1>
            <p className="text-base md:text-lg text-gray-700">Evaluate three underwater mining sites and select two for investment, balancing revenue, purity levels, and ecological sustainability while managing limited capital and avoiding regulatory breaches.</p>
            <div className="mt-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                For the best experience, adjust your browser zoom to 75-80%. This ensures all case elements display properly and are easy to interact with.
              </p>
            </div>
            {(!hasStarted || timeExpired || finishedEarly) && (
              <button
                onClick={() => {
                  setHasStarted(true);
                  setTimeLeft(1200);
                  setAnswers({});
                  setFeedback({});
                  setReveal({});
                  setTimeExpired(false);
                  setFinishedEarly(false);
                  setIsComplete(false);
                }}
                className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                {!hasStarted ? 'Start Case' : 'Restart Case'}
                <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                  {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                </div>
              </button>
            )}
          </div>
        </div>
        <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
          {hasStarted && (timeExpired || isComplete) && (
            <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
              Math.round((totalCorrect / totalQuestions) * 100) < 65 
              ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
              : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]'
            }`}>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                  Math.round((totalCorrect / totalQuestions) * 100) < 65
                  ? 'from-red-50/50 to-white/50'
                  : 'from-blue-50/50 to-white/50'
                }`}></div>
                <div className="relative space-y-2.5">
                  <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                    <span className="text-lg">📊</span> Final Score
                  </h2>
                  <p className="text-[15px] text-gray-700">
                    Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                  </p>
                  <p className="text-[15px] text-gray-700">
                    Report: {numReportCorrect}/{numReportQuestions}
                  </p>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className={`rounded-full h-2 transition-all duration-300 ${
                        Math.round((totalCorrect / totalQuestions) * 100) < 65
                        ? 'bg-red-500'
                        : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.round((totalCorrect / totalQuestions) * 100)}%` }}
                    ></div>
                  </div>
                  <p className={`text-[14px] ${
                    Math.round((totalCorrect / totalQuestions) * 100) < 65
                    ? 'text-red-600'
                    : 'text-gray-600'
                  }`}>
                    Total: {totalCorrect}/{totalQuestions} ({Math.round((totalCorrect / totalQuestions) * 100)}% Accuracy)
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
            <img 
              src={case14Image} 
              alt="SkyPortX Airport Case" 
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
          {isNotebookOpen && hasBeenOpened && (
            <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
              <div className="p-3 border-b border-gray-100">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                    </p>
                  </div>
                  <button 
                    className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                      hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => hasStarted && handleNotebookToggle()}
                    disabled={!hasStarted}
                    title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                    </svg>
                    {hasStarted && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                        <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                          Click to collapse notebook
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <div 
                className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                onDragOver={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-blue-50');
                }}
                onDragLeave={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-blue-50');
                }}
                onDrop={handleDrop}
                contentEditable={hasStarted}
                suppressContentEditableWarning={true}
                style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
              >
                {!hasStarted && (
                  <div className="text-gray-500 text-sm italic">
                    Click "Start Case" to begin taking notes...
                  </div>
                )}
                {notebookItems.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                    contentEditable={false}
                  >
                    <div className="flex-1">
                      <div className="text-xs font-medium text-gray-900">{item.value}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                    </div>
                    <button
                      onClick={() => hasStarted && removeNotebookItem(index)}
                      className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {hasStarted && (
          <div className="fixed top-[85px] right-10 md:right-20 z-50">
            <div 
              className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-pulse-dramatic border-2 border-red-500'
                  : ''
              }`}
            >
              <span className={`text-base ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-bounce'
                  : ''
              }`}>⌛</span>
              <span className={`${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'text-red-500 font-bold'
                  : 'text-blue-600'
              }`}>
                Time Left: {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Problem Section - Enhanced design */}
      <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="pt-20 pb-24 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="md:col-span-2 relative group" style={{ 
            paddingRight: 'calc(460px + 1.25rem)',
            maxWidth: 'calc(100% - 100px)'
          }}>
            <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-blue-50/50 opacity-50" 
                   style={{ 
                     clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                     background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
                   }}>
              </div>
            </div>
            <div className="relative p-8">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  📝
                </span>
                Problem
              </h2>
              <div className="space-y-4 prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed">DeepCurrent Minerals is a deep-sea extraction company evaluating three underwater mining sites: Site Kora, Site Vesta, and Site Luno. Each site offers a different mix of rare minerals, common minerals, and waste material, which impacts the purity percentage, processing cost, and environmental penalty.</p>
                <p className="text-gray-700 leading-relaxed">The goal is to select two out of the three sites for investment, balancing revenue, purity levels, and ecological sustainability. The company has a limited capital budget and must avoid regulatory breaches tied to high environmental impact scores.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investigation Section */}
      <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              🔍
            </span>
            Investigation
          </h2>
          <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
            <span className="bg-blue-50 px-2 py-0.5 rounded">💡 Tip:</span>
            Drag and drop highlighted text into your notebook to collect important data points
          </p>
          <div className="flex gap-12 pr-[200px]">
            {/* Investigation Text */}
            <div className="w-[700px]">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                <p className="text-lg leading-relaxed">
                  Each site varies in yield composition, mineral value, extraction cost, and environmental risk. Site Kora has <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "30%", "Kora Rare Minerals")}
                  >30%</span> rare minerals, <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "50%", "Kora Common Minerals")}
                  >50%</span> common minerals, and <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "20%", "Kora Waste")}
                  >20%</span> waste material.
                </p>
                <p className="text-lg leading-relaxed">
                  Site Vesta contains <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "20%", "Vesta Rare Minerals")}
                  >20%</span> rare minerals, <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "40%", "Vesta Common Minerals")}
                  >40%</span> common minerals, and <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "40%", "Vesta Waste")}
                  >40%</span> waste material.
                </p>
                <p className="text-lg leading-relaxed">
                  Site Luno shows <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "25%", "Luno Rare Minerals")}
                  >25%</span> rare minerals, <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "35%", "Luno Common Minerals")}
                  >35%</span> common minerals, and <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "40%", "Luno Waste")}
                  >40%</span> waste material.
                </p>
                <p className="text-lg leading-relaxed mt-4">
                  The average value per ton varies: Kora at <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$12,000", "Kora Value/ton")}
                  >$12,000</span>, Vesta at <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$9,000", "Vesta Value/ton")}
                  >$9,000</span>, and Luno at <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$10,500", "Luno Value/ton")}
                  >$10,500</span>.
                </p>
                <p className="text-lg leading-relaxed">
                  Processing costs per ton are: Kora <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$4,800", "Kora Processing Cost")}
                  >$4,800</span>, Vesta <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$3,000", "Vesta Processing Cost")}
                  >$3,000</span>, and Luno <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "$3,600", "Luno Processing Cost")}
                  >$3,600</span>.
                </p>
                <p className="text-lg leading-relaxed">
                  Environmental scores (0-100) are: Kora <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "35", "Kora Environmental Score")}
                  >35</span>, Vesta <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "20", "Vesta Environmental Score")}
                  >20</span>, and Luno <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "60", "Luno Environmental Score")}
                  >60</span>.
                </p>
              </div>
            </div>

            {/* Interactive Notebook */}
            {isNotebookOpen ? (
              <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                      </p>
                    </div>
                    <button 
                      className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                        hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasStarted && handleNotebookToggle()}
                      disabled={!hasStarted}
                      title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                      </svg>
                      {hasStarted && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                            Click to collapse notebook
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div 
                  className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                  onDragOver={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={handleDrop}
                  contentEditable={hasStarted}
                  suppressContentEditableWarning={true}
                  style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                >
                  {!hasStarted && (
                    <div className="text-gray-500 text-sm italic">
                      Click "Start Case" to begin taking notes...
                    </div>
                  )}
                  {notebookItems.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                      contentEditable={false}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                      </div>
                      <button
                        onClick={() => hasStarted && removeNotebookItem(index)}
                        className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={handleNotebookToggle}
                className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                  hasStarted 
                    ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasStarted}
              >
                <div className="relative">
                  {hasStarted && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                  )}
                  <svg 
                    className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-blue-600' : 'text-gray-400'} transition-colors duration-300`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {hasStarted && (
                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                      Open notebook ({notebookItems.length} notes collected)
                    </div>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Exhibits Section */}
      <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
        <div className="container pl-[72px] pr-[200px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              📈
            </span>
            Exhibits
          </h2>
          <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze the data.</p>
          
          {/* Exhibit Navigation */}
          <div className="flex items-center gap-3 mb-4">
            {['A', 'B', 'C', 'D'].map((exhibit) => (
              <button
                key={exhibit}
                onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                disabled={!hasStarted}
                className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                  !hasStarted 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                    : currentExhibit === exhibit
                      ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-white text-gray-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105'
                }`}
              >
                {exhibit}
              </button>
            ))}
          </div>

          {/* Exhibit Display */}
          <div className={`bg-white rounded-xl shadow-sm transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
            <div className="p-4">
              {currentExhibit === 'A' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="text-blue-600">📊</span>
                    Exhibit A: Purity Score by Site
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
              </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibitA} 
                      alt="Purity Score by Site" 
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-2">
                    This chart shows the purity scores for each mining site.
                  </p>
                </>
              )}

              {currentExhibit === 'B' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-600">💰</span>
                    Exhibit B: Profit Index by Site
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibitB} 
                      alt="Profit Index by Site" 
                      className="w-full h-full object-contain"
                    />
            </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Comparison of profit indices across the mining sites before penalties.
                  </p>
                </>
              )}

              {currentExhibit === 'C' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-600">⚠️</span>
                    Exhibit C: Environmental Risk vs Profit
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
              </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibitC} 
                      alt="Environmental Risk vs Profit" 
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Relationship between environmental risk scores and profit for each site.
                  </p>
                </>
              )}

              {currentExhibit === 'D' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-600">📈</span>
                    Exhibit D: Risk-Adjusted Profit
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibitD} 
                      alt="Risk-Adjusted Profit" 
                      className="w-full h-full object-contain"
                    />
            </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Final profit figures after adjusting for environmental risk penalties.
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Static Exhibit Grid */}
            {/* ... existing exhibit grid code ... */}
          </div>
        </div>
      </section>

      {/* Analysis Section */}
      <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gray-50/80">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              🧠
            </span>
            Analysis
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            {questionData.map((q) => (
              <div 
                key={q.id} 
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                    {q.id}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-blue-600 mb-4">
                      {q.question}
                    </h3>
                    {q.type === 'text' ? (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                      <input
                        type="text"
                            value={answers[q.id] || ''}
                            onChange={(e) => handleInput(q.id, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                            className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                            placeholder="Type your answer and press Enter..."
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      />
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'multi' ? (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-3">
                          {q.parts.map((part, index) => (
                            <div key={index} className="flex gap-3">
                              <div className="flex-grow">
                                <label className="block text-sm font-medium text-gray-700 mb-1">{part.label}</label>
                                <input
                                  type="text"
                                  value={((answers[q.id] || {})[index] || '')}
                                  onChange={(e) => handleInput(q.id, e.target.value, index)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                  placeholder={`Enter ${part.label.toLowerCase()}...`}
                                  disabled={!hasStarted || timeExpired || finishedEarly}
                                />
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">
                              🔍 Answer: 
                              {q.id === 8 ? (
                                <>
                                  <br />
                                  <strong>First Site</strong>: Either Vesta or Luno
                                  <br />
                                  <strong>Second Site</strong>: Either Vesta or Luno
                                  <br />
                                  <span className="text-sm italic">(Must enter both sites, but in any order)</span>
                                </>
                              ) : q.parts ? (
                                q.parts.map((part, index) => (
                                  <span key={index}>
                                    <br />
                                    <strong>{part.label}</strong>: {part.correctAnswers ? part.correctAnswers.join(' or ') : part.correctAnswer}
                                  </span>
                                ))
                              ) : (
                                <strong>{q.correctAnswer}</strong>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'mcq' ? (
                      <div className="space-y-2">
                        {q.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleMCQSelect(q.id, option)}
                            className={`w-full text-left px-4 py-2 rounded-lg border transition-all duration-300 transform hover:scale-102 ${
                              answers[q.id] === option
                                ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white border-transparent'
                                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            {option}
                          </button>
                        ))}
                        <div className="flex gap-2 mt-3">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                        </div>
                      )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                      )}
                    </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

              {finishedEarly && (
                <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Report Section */}
      <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gradient-to-br from-blue-50/40 via-blue-50/20 to-white">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              📊
            </span>
            Report
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
              <div className="flex items-start gap-4">
                <div className="flex-grow">
                  <div className="space-y-3">
                    <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                      DeepCurrent Minerals evaluated three underwater sites for mineral extraction. The two primary selection criteria were profitability and environmental risk. According to Exhibit A, <input
                        type="text"
                        value={answers['report_site_purity'] || ''}
                        onChange={(e) => handleInput('report_site_purity', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_site_purity'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_site_purity'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> scored the highest in purity, while Exhibit B showed <input
                        type="text"
                        value={answers['report_profit_index'] || ''}
                        onChange={(e) => handleInput('report_profit_index', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_profit_index'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_profit_index'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> had the strongest profit index before penalties.
                    </div>
                    <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                      However, due to an environmental score of <input
                        type="text"
                        value={answers['report_env_score'] || ''}
                        onChange={(e) => handleInput('report_env_score', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_env_score'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_env_score'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      />, the site <input
                        type="text"
                        value={answers['report_site_penalty'] || ''}
                        onChange={(e) => handleInput('report_site_penalty', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_site_penalty'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_site_penalty'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> incurred a 25% penalty (see Exhibit C), dropping its final profit to $<input
                        type="text"
                        value={answers['report_adjusted_profit'] || ''}
                        onChange={(e) => handleInput('report_adjusted_profit', e.target.value)}
                        className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_adjusted_profit'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_adjusted_profit'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> (Exhibit D).
                    </div>
                    <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                      We recommend investing in <input
                        type="text"
                        value={answers['report_site_1'] || ''}
                        onChange={(e) => handleInput('report_site_1', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_site_1'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_site_1'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> and <input
                        type="text"
                        value={answers['report_site_2'] || ''}
                        onChange={(e) => handleInput('report_site_2', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_site_2'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_site_2'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> as they offer the most balanced combination of high purity and profit, acceptable environmental scores (below 50), and minimal penalty risk and long-term ROI.
                    </div>
                    <div className="flex gap-2">
                      {(!timeExpired && !finishedEarly) && (
                        <button
                          onClick={() => handleReportSubmit()}
                          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                          disabled={!hasStarted || timeExpired || finishedEarly}
                        >
                          Submit Answers
                        </button>
                      )}
                      {(timeExpired || finishedEarly) && (
                        <button
                          onClick={() => handleReportReveal()}
                          className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                          See Answers
                        </button>
                      )}
                    </div>
                    {(timeExpired || finishedEarly) && feedback['report'] && (
                      <div className={`mt-4 p-3 rounded-lg ${
                        Object.values(feedback).every(f => f === 'correct') 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {Object.values(feedback).every(f => f === 'correct') 
                          ? 'All answers are correct!' 
                          : 'Some answers are incorrect. Please check your answers.'}
                      </div>
                    )}
                    {reveal['report'] && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-blue-700">
                          🔍 Answers:<br/>
                          Highest purity: Kora<br/>
                          Highest profit index: Kora<br/>
                          Environmental score: 60<br/>
                          Site with penalty: Luno<br/>
                          Adjusted profit: 211,875<br/>
                          Best sites for investment: Kora, Vesta
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {timeExpired && !finishedEarly && (
              <div className="mt-6 text-red-600 text-base font-semibold">
                ❌ Time's up! You can still view the answers by clicking "See Answer".
              </div>
            )}
            {finishedEarly && (
              <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed the report before the timer ran out! Amazing work!
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recommendation Section - Subtle off-white */}
      <div id="recommendation" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gray-50/80">
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="md:col-span-2 md:pr-[460px]">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                💡
              </span>
              Recommendation
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="mr-3 text-xl">💰</span>
                <span>Prioritize Kora and Vesta for investment based on optimal profit-risk balance</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">⚖️</span>
                <span>Maintain environmental scores below 50 to avoid regulatory penalties</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">🌱</span>
                <span>Focus on sites with higher rare mineral percentages and lower waste ratios</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">📊</span>
                <span>Monitor purity scores and processing costs to optimize extraction value</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Case14Page; 

<style jsx global>{`
    @keyframes pulse-dramatic {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.15);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
      }
    }
    
    .animate-pulse-dramatic {
      animation: pulse-dramatic 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0) scale(1);
      }
      50% {
        transform: translateY(-5px) scale(1.2);
      }
    }

    .animate-bounce {
      animation: bounce 0.5s ease infinite;
    }
  `}</style>